// "Log event" (S6): the day an event the rules wait on happened. Logging it
// meets the deliverable that dates the event and mints every date the rules
// owe after it; the dates projected from its plan give way to them. The write
// is LogContractEvent, so it lands in History like every contract-model write.
//
// The date never defaults to today (D98): the person states the day.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDateField, SpecraFormField, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useLogContractEvent } from '../hooks';
import { cmDate } from '../lib/format';
import { eventLabel, loggableEvents, mintReceipt } from '../lib/rule';
import { useCmLookups } from '../lookups';
import { CmDrawer } from './CmDrawer';
/** contract_events.label, which holds the note. */ const NOTE_MAX = 255;
export function LogEventDrawer({ programId, kind: initialKind = '', onClose }) {
    const lk = useCmLookups(programId);
    // The preselected event stays visible even while the lookups load, so the
    // select never hides the kind it will log.
    const derived = loggableEvents(lk.rules, lk.duties);
    const events = initialKind && !derived.some((e)=>e.kind === initialKind) ? [
        ...derived,
        {
            kind: initialKind,
            label: eventLabel(initialKind)
        }
    ] : derived;
    const [kind, setKind] = useState(initialKind);
    const [occurredOn, setOccurredOn] = useState('');
    const [note, setNote] = useState('');
    const label = events.find((e)=>e.kind === kind)?.label ?? kind;
    const log = useLogContractEvent(programId, {
        errors: 'inline',
        success: (r)=>`${label} logged for ${cmDate(r.event?.occurredOn)}. ${mintReceipt(r.result?.mint)}`
    });
    const problem = !kind ? 'Pick the event.' : !occurredOn ? 'Enter the day it happened.' : null;
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Log event",
        description: "The day it happened. Dates the contract counts from this event are set from it, and the ones projected from its plan give way.",
        onClose: onClose,
        busy: log.isPending,
        testId: Testids.cmDuty.logEventDrawer,
        footerProps: {
            confirmLabel: 'Log event',
            onConfirm: ()=>log.mutate({
                    kind,
                    occurredOn,
                    label: note.trim()
                }, {
                    onSuccess: ()=>onClose()
                }),
            confirmDisabled: !!problem || log.isPending,
            loading: log.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuty.logEventSave
            },
            leading: problem ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: problem
            }) : undefined
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Event",
                    required: true,
                    hint: "The events this contract's rules count from.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: kind,
                        onChange: (e)=>setKind(e.target.value),
                        "data-testid": Testids.cmDuty.logEventKind,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Pick the event…"
                            }),
                            events.map((e)=>/*#__PURE__*/ _jsx("option", {
                                    value: e.kind,
                                    children: e.label
                                }, e.kind))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Happened on",
                    required: true,
                    value: occurredOn,
                    onValueChange: setOccurredOn,
                    "data-testid": Testids.cmDuty.logEventDate
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note",
                    hint: "e.g. who attended, or where it is recorded.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        maxLength: NOTE_MAX,
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        "data-testid": Testids.cmDuty.logEventNote
                    })
                }),
                log.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(log.error, 'Could not log the event')
                }) : null
            ]
        })
    });
}
